import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import DropdownMenu from '../../components/dropdown/DropdownMenu'
import SvgFragment from '../SvgFragment'
import DropdownItem from '../../components/dropdown/DropdownItem'
import { useAppHandlers } from '../../services/actions/app/useAppHandlers'
import { AppRoutes, SidebarTypes } from '../../interfaces/store/appStore'
import { IConnection } from '../../interfaces/models/connection'
import Row from '../../components/Row'
import { useConnectionsHandlers } from '../../services/actions/connections/useConnectionsHandlers'
import { useAnalyticsHandlers } from '../../services/actions/analytics/useAnalyticsHandlers'
import posthog from 'posthog-js';
import { useAuthStore } from '../../stores/auth/useAuthStore'
import { hasCampaignsAccess } from '../../services/campaigns/campaignsNavAccess'
import { useCampaignsBulkActionAnnouncement } from '../../services/campaigns/useCampaignsBulkActionAnnouncement'
import { CONNECTION_ACTION_BAR_BP, CONNECTION_ACTION_MENU_ORDER } from '../../pages/canvas/components/connections/connectionActionBarBreakpoints'
import { useAuthHandlers } from '../../services/actions/auth/useAuthHandlers'
import { useConnectionsStore } from '../../stores/connections/useConnectionsStore'
import { useAppStore } from '../../stores/application/useAppStore'
import Column from '../../components/Column'
import ActionText from '../../components/ActionText'
import { styles } from '../../styles/themes/style'
import { useExternalHandlers } from '../../services/actions/navigation/useExternalHandlers'
import { useNavigate } from 'react-router-dom'
import useMediaQuery from '../../components/useMediaQuery'
import { isLNFirst } from './../../utils';
import { useHubspotHandlers } from '../../services/actions/hubspot/useHubspotHandlers';
import { useConnectionsActions } from '../../stores/connections/useConnectionsActions';
import TooltipWrapper from '../../components/TooltipWrapper';
import momentTimezone from 'moment-timezone';


const connectionIdsFromRows = (rows: any[] | undefined): string[] =>
    (rows ?? []).flatMap((row) => {
        if (typeof row === 'string' || typeof row === 'number') return [String(row)];
        if (row?._id) return [String(row._id)];
        return [];
    });

interface IBulkConnectionActionsDropdownProps {
    className?: string,
    selectedConnections?: IConnection[],
    selectedAllConnections?: boolean,
    onClearSelection?: () => void
}

const BulkConnectionActionsDropdown: FunctionComponent<IBulkConnectionActionsDropdownProps> = ({ className, selectedConnections, selectedAllConnections, onClearSelection }) => {
    const { openSidebar, setExportConnectionsModalHandler, setCalculateEnrichmentModalHandler, setExportToHubspotModalHandler, setBulkEditTagsModal, setCreateFeedModal, setScheduleDisconnectModalHandler, setConfirmationModal, hideModal, showErrorToast, showSuccessToast, showInfoToast, setAddToKanbanModal } = useAppHandlers()
    const { toggleBulkHideConnectionsHandler, exportToCSVHAndler, sendMessageToConnectionsHandler, getSelectedConnections, scheduleConnectionsRemovalHandler, bulkAddConnectionsToKanbanHandler, openAddToCampaignFlow, generateAiRecommendationsHandler, getConnectionAiRecommendationsHandler } = useConnectionsHandlers()
    const { getTotalConnectionsOverviewAnalytics } = useAnalyticsHandlers()
    const { store: { user, disconnectedConnectionsCount, isLoggedToLinkedin, workspace, trial, subscription } } = useAuthStore()
    const { store: { extensionInstalled, extensionVersion } } = useAppStore()
    const { setDisconnectedConnectionsCountHandler, searchPeopleSduiHandler } = useAuthHandlers()
    const { showCampaignsAnnouncementIfNeeded, shouldShowAnnouncementFor } = useCampaignsBulkActionAnnouncement()
    const { store: { connectionsParameters: {total}, connectionsFilter }} = useConnectionsStore()
    const { patchConnection } = useConnectionsActions()
    const [enableLinkedinAction, setEnableLinkedinAction] = useState<boolean>(false)
    const [scanMutualsLoading, setScanMutualsLoading] = useState(false)
    // no component state for removal selection to avoid losing it when dropdown unmounts
    const { saveOnboardingData } = useAuthHandlers()
    const { visitChromeStore, openLinkedinWebsite, updateLeadDeltaExtension } = useExternalHandlers()
    const { exportToHubspotHandler } = useHubspotHandlers()
    const navigate = useNavigate()

    // Must match ConnectionControlBar styled breakpoints (connectionActionBarBreakpoints.ts)
    const isScreenMaxWidthSmallDesktop = useMediaQuery(`(max-width: ${CONNECTION_ACTION_BAR_BP.smallDesktop}px)`);
    const isScreenMaxWidthMutualsToDropdown = useMediaQuery(`(max-width: ${CONNECTION_ACTION_BAR_BP.mutuals}px)`);
    const isScreenMaxWidthEnrichToDropdown = useMediaQuery(`(max-width: ${CONNECTION_ACTION_BAR_BP.enrich}px)`);
    const isScreenMaxWidthCampaignsToDropdown = useMediaQuery(`(max-width: ${CONNECTION_ACTION_BAR_BP.campaigns}px)`);
    const isScreenMaxWidthMessageToDropdown = useMediaQuery(`(max-width: ${CONNECTION_ACTION_BAR_BP.message}px)`);
    const isScreenMobile = useMediaQuery("(max-width: 575px)");
    const isExtensionVersionCorrect = extensionVersion === process.env.REACT_APP_VERSION

    // Must match backend's America/New_York date logic (and optimistic updates) to keep limits in sync.
    const todayKey = momentTimezone.tz(new Date(), "America/New_York").format("YYYY-MM-DD");
    const dailyMax = Number(process.env.REACT_APP_AI_RECOMMENDATIONS_DAILY_LIMIT ?? 20) || 20;
    const usedToday = (user as any)?.aiRecommendationsAnalytics?.dailyDate === todayKey
        ? (Number((user as any)?.aiRecommendationsAnalytics?.dailyCount ?? 0) || 0)
        : 0;
    const remainingToday = Math.max(0, dailyMax - usedToday);

    // Bulk selection limit is tied directly to remaining daily limit.
    const effectiveBulkCap = remainingToday;

    const selectionCountForBulkAi = selectedConnections?.length ?? 0;
    const isAiSelectionOverLimit = selectionCountForBulkAi > effectiveBulkCap && effectiveBulkCap > 0;
    const canUseWarmIntroAi =
        !!workspace?.aiChatEnabled ||
        !!trial ||
        (!!workspace?.isBusiness && subscription?.status === 'active');

    useEffect(() => {
        setDisconnectedConnectionsCountHandler(disconnectedConnectionsCount);
     }, [disconnectedConnectionsCount])

    useEffect(() => {
        getTotalDisconnected()
    }, [])

    useEffect(() => {
        if(selectedConnections?.length === 1) {
            setEnableLinkedinAction(isLNFirst(selectedConnections?.[0], user?._id))
        } else {
            setEnableLinkedinAction(true)
            for(const conn of selectedConnections || []) {
                if(!isLNFirst(conn, user?._id)){
                    setEnableLinkedinAction(false);
                    break;
                }
            }
        }
    }, [selectedConnections])

    const getTotalDisconnected = async () => {
        const result = await getTotalConnectionsOverviewAnalytics()
        setDisconnectedConnectionsCountHandler((result.find(item => item.version === 'connections-disconnected-total'))?.value || 0)
    }

    const exportConnections = useCallback(async (includeTags: boolean, includeNotes: boolean, exportAll: boolean) => {
        if(exportAll){
            await exportToCSVHAndler(includeTags, includeNotes)
        }
        else {
            await exportToCSVHAndler(includeTags, includeNotes, selectedConnections)
        }
    }, [selectedConnections])

    const exportToHubspot = useCallback(async (
        includeTags: boolean, 
        includeNotes: boolean, 
        exportAll: boolean, 
        useDefaultMapping: boolean, 
        exportType: 'contacts' | 'list' | 'workflow' | 'deals',
        exportDetails?: {
            listId?: string;
            workflowId?: string;
            dealStage?: string;
            dealName?: string;
            amount?: number;
            pipelineId?: string;
        }
    ) => {
        try {
            // Base payload
            const basePayload: any = {
                connectionIds: exportAll ? [] : selectedConnections?.map(connection => connection._id) || [],
                exportAll: exportAll,
                includeTags,
                includeNotes,
                useDefaultMapping,
                exportType,
                // Spread export details to include all type-specific fields
                ...exportDetails
            };

            // Handle field mapping flow
            if (useDefaultMapping) {
                // Use existing mappings and import directly
                const result = await exportToHubspotHandler(basePayload);
                
                if (result?.success) {
                    showSuccessToast({ message: result.message || 'Successfully exported to HubSpot' });
                }
                else {
                    showErrorToast({ message: 'Error exporting contact to HubSpot' });
                }
            } else {
                // Navigate to mapping page with export parameters for all types
                navigate(AppRoutes.HUBSPOT_MAPPING, { 
                    state: { 
                        exportParams: basePayload 
                    } 
                });
            }
            
        } catch (error) {
            console.error('Export to HubSpot error:', error);
            showErrorToast({ message: 'Error exporting contacts to HubSpot' });
        }
    }, [selectedConnections, selectedAllConnections, total, exportToHubspotHandler, navigate, showSuccessToast, showErrorToast]) 

    const user1stDegreeSelectedConnections = selectedConnections?.filter(
        (selectedConnection) =>
            isLNFirst(selectedConnection, user?._id) && // use only Ln-1st connections
            !selectedConnection?.autoTagsArray?.some((tagArrayItem: any) => tagArrayItem?._id === user?._id && // skip disconnected connections
                    tagArrayItem?.autoTags?.some(
                        (tag: any) => tag?.title === "Disconnected"
                    )
            )
    ) || [];

    const hubspotEnabled = workspace?.hubspotEnabled === true;

    // Campaigns announcement overrides the bulk action items' enabled state so the modal can open on click.
    const bulkMessageEnabled = Boolean(user1stDegreeSelectedConnections?.length && !workspace?.isStarter && user1stDegreeSelectedConnections?.length <= 100 && extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect);
    const announceBulkMessage = shouldShowAnnouncementFor('bulkMessage');
    const bulkRemoveEnabled = Boolean(user1stDegreeSelectedConnections?.length && extensionInstalled && isLoggedToLinkedin && isExtensionVersionCorrect);
    const announceBulkDisconnect = shouldShowAnnouncementFor('bulkDisconnect');

    const bulkConnectionActions: any[] = [
        {
            icon: 'message',
            title: `Message`,
            slug: 'messageProfile',
            enabled: bulkMessageEnabled || announceBulkMessage,
            visible: isScreenMobile || isScreenMaxWidthMessageToDropdown,
            destructive: false,
            onClickHandler: () => {
                const openBulkMessageSidebar = () => openSidebar(SidebarTypes.BULK_MESSAGE, {
                    formType: 'message',
                    onMessageSaveHandler: (message: string) =>
                        sendMessageToConnectionsHandler(user1stDegreeSelectedConnections, message)
                })
                if (showCampaignsAnnouncementIfNeeded('bulkMessage', bulkMessageEnabled ? openBulkMessageSidebar : undefined)) return
                if (!bulkMessageEnabled) return
                openBulkMessageSidebar()
            }
        },
        {
            icon: 'campaignGray',
            title: 'Add to campaign',
            slug: 'addToCampaign',
            enabled: Boolean(selectedConnections?.length) || Boolean(selectedAllConnections),
            visible: hasCampaignsAccess(workspace, trial) && (isScreenMobile || isScreenMaxWidthCampaignsToDropdown),
            showInstallExt: false,
            destructive: false,
            onClickHandler: async () => {
                await openAddToCampaignFlow(selectedConnections || [], {
                    selectedAllConnections,
                    total,
                    onClose: onClearSelection,
                });
            }
        },
        {
            icon: 'enrichGrey',
            iconDisabled: 'enrichDisabled',
            title: 'Enrich',
            slug: 'enrichConnection',
            enabled: selectedConnections?.length,
            visible: isScreenMobile || isScreenMaxWidthEnrichToDropdown,
            onClickHandler: () => onClickEnrichHandler()
        },
        {
            icon: 'scan',
            title: scanMutualsLoading ? 'Finding intros...' : 'Find intros',
            slug: 'bulkScanMutualConnections',
            enabled: canUseWarmIntroAi && remainingToday > 0 && (Boolean(selectedConnections?.length) || Boolean(selectedAllConnections)) && !isAiSelectionOverLimit && !scanMutualsLoading,
            visible: isScreenMobile || isScreenMaxWidthMutualsToDropdown,
            showInstallExt: false,
            destructive: false,
            tooltipMessage:
                !canUseWarmIntroAi
                    ? 'Upgrade to Growth to unlock this feature'
                    : (selectedConnections?.length === 0 && !selectedAllConnections)
                    ? 'Select contacts to find intros'
                    : remainingToday <= 0
                        ? `You\u2019ve reached your daily limit of ${dailyMax} warm intros.`
                        : isAiSelectionOverLimit
                            ? `You can find intros for up to ${effectiveBulkCap} contacts right now`
                            : 'Find intros',
            onClickHandler: () => scanMutualConnectionsHandler()
        },
        {
            icon: 'feed',
            iconDisabled: 'feedDisabled',
            title: 'Add to feed',
            slug: 'customFeed',
            enabled: selectedConnections?.length,
            visible: isScreenMobile || isScreenMaxWidthMutualsToDropdown,
            onClickHandler: async () => {
                if (selectedAllConnections) {
                    const rows = await getSelectedConnections(total, true, false);
                    const ids = connectionIdsFromRows(rows);
                    if (!ids.length) {
                        showInfoToast({ message: 'Could not load all selected contacts. Try again.' });
                        return;
                    }
                    setCreateFeedModal(ids);
                    return;
                }
                setCreateFeedModal(selectedConnections?.map(c => c._id));
            }
        },
        {
            icon: 'profileRemove',
            title: `Remove`,
            slug: 'remove',
            enabled: bulkRemoveEnabled || announceBulkDisconnect,
            visible: isScreenMobile || isScreenMaxWidthSmallDesktop,
            destructive: true,
            onClickHandler: () => clickOnRemoveHandler()
        },
        {
            icon: 'label',
            title: `Tag`,
            slug: 'applyRemove',
            enabled: selectedConnections?.length,
            visible: isScreenMobile || isScreenMaxWidthSmallDesktop,
            destructive: false,
            onClickHandler: () => setBulkEditTagsModal(selectedConnections?.map(connection => connection._id), selectedAllConnections)
        },
        {
            icon: 'hide',
            title: `Hide`,
            slug: 'hide',
            enabled: selectedConnections?.length && connectionsFilter?.hiddenConnectionsValue?.value !== 'yes',
            visible: true,
            destructive: true,
            onClickHandler: () => onClickHideHandler(true)
        },
        {
            icon: 'show',
            title: `Unhide`,
            slug: 'unhide',
            enabled: selectedConnections?.length && connectionsFilter?.hiddenConnectionsValue?.value !== 'no',
            visible: true,
            showInstallExt: false,
            destructive: false,
            onClickHandler: () => onClickHideHandler(false)
        },
        {
            icon: 'kanban',
            iconDisabled: 'kanbanDisabled',
            title: 'Add to pipeline',
            slug: 'addToKanban',
            enabled: selectedConnections?.length && !workspace?.isStarter,
            visible: true,
            showInstallExt: workspace?.isStarter,
            destructive: false,
            onClickHandler: async () => {
                const kanbanIds = workspace?.kanbans || [];

                // If there's exactly one pipeline in this workspace, add directly
                if (kanbanIds.length === 1) {
                    await bulkAddConnectionsToKanbanHandler(selectedConnections || [], [kanbanIds[0]]);
                    return;
                }

                // Otherwise, open modal to choose pipelines
                setAddToKanbanModal(selectedConnections);
            }
        },
        {
            icon: 'import',
            title: 'Import CSV',
            slug: 'import',
            enabled: !workspace?.isStarter,
            visible: true,
            showInstallExt: workspace?.isStarter,
            destructive: false,
            onClickHandler: () => navigate(AppRoutes.CSV_IMPORT)
        },
        {
            icon: 'export',
            title: 'Export contacts',
            slug: 'export',
            enabled: !workspace?.isStarter,
            visible: true,
            showInstallExt: workspace?.isStarter,
            destructive: false,
            onClickHandler: () => {
                setExportConnectionsModalHandler(
                    exportConnections,
                    selectedAllConnections ? total : selectedConnections?.length ?? undefined,
                    selectedAllConnections
                )
            }
        },
        {
            icon: 'exportHubspot',
            title: 'Export to HubSpot',
            slug: 'exportHubspot',
            enabled: selectedConnections?.length && hubspotEnabled,
            visible: true,
            showInstallExt: workspace?.isStarter,
            destructive: false,
            onClickHandler: () => {
                setExportToHubspotModalHandler(
                    exportToHubspot,
                    selectedAllConnections ? total : selectedConnections?.length ?? undefined,
                    selectedAllConnections
                )
            }
        }
    ];

    const installExtensionHandler = useCallback(async () => {
        await Promise.all([
            saveOnboardingData({
                startedExtensionInstallation: true,
            }),
            (async () => visitChromeStore())(),
        ]);
        
    },[]);

    const calculateEnrichmentHandler = useCallback(async () => {
        console.log('calculateEnrichmentHandler');
        
        //bulkEnrichConnectionsHandler(user1stDegreeSelectedConnections)
        
    }, [])

    const onClickEnrichHandler = async () => {
        if (selectedAllConnections) {
                let result = await getSelectedConnections(total, true, false);
                setCalculateEnrichmentModalHandler(
                    calculateEnrichmentHandler,
                    () => {},
                    selectedAllConnections,
                    result
                );
            } else {
                setCalculateEnrichmentModalHandler(
                    calculateEnrichmentHandler,
                    () => {},
                    selectedAllConnections,
                    selectedConnections
                );
        }
    }

    const onClickHideHandler = async (hideValue: boolean) => {
        if (selectedAllConnections) {
            await toggleBulkHideConnectionsHandler([], hideValue);
        } else {
            await toggleBulkHideConnectionsHandler(selectedConnections || [], hideValue);
        }
        // Clear selection only after hide (not unhide)
        if (hideValue && onClearSelection) {
            onClearSelection();
        }
    }

    // Poll until the backend AI generation completes (backend generate is fire-and-forget).
    // Returns the final status + match count (AI mutual profiles length).
    const pollUntilDone = useCallback(async (connectionId: string): Promise<{ status: string; matches: number }> => {
        const maxAttempts = 24; // 24 × 5 s = 2 min max
        const delayMs = 5000;
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise((res) => setTimeout(res, delayMs));
            try {
                const latest: any = await getConnectionAiRecommendationsHandler(connectionId);
                // Only stop polling on terminal states. Some backends may briefly return "idle"
                // while generation is still in progress.
                if (latest?.status && (latest.status === 'ready' || latest.status === 'error')) {
                    const matches = latest?.linkedinAiMutualProfilesCount ?? (Array.isArray(latest?.linkedinAiMutualProfiles) ? latest.linkedinAiMutualProfiles.length : 0);
                    patchConnection(connectionId, {
                        aiRecommendationsStatus: latest.status as any,
                        linkedinAiMutualProfilesCount: latest.linkedinAiMutualProfilesCount ?? (Array.isArray(latest.linkedinAiMutualProfiles) ? latest.linkedinAiMutualProfiles.length : 0)
                    } as any);
                    return { status: latest.status, matches };
                }
            } catch {
                // retry on transient errors
            }
        }
        patchConnection(connectionId, { aiRecommendationsStatus: 'error' as any });
        return { status: 'error', matches: 0 };
    }, [getConnectionAiRecommendationsHandler, patchConnection]);

    const scanMutualConnectionsHandler = useCallback(async () => {
        if (scanMutualsLoading) return;

        setScanMutualsLoading(true);
        try {
            let connectionsToScan = selectedConnections || [];

            if (selectedAllConnections) {
                connectionsToScan = await getSelectedConnections(total, true, false);
            }

            const validConnections = connectionsToScan.filter(
                (connection) => connection?._id && connection?.profileId && connection?.publicIdentifier
            );

            if (!validConnections.length) {
                showInfoToast({ message: 'No valid contacts selected for mutual scan.' });
                return;
            }

            const allowedCount = Math.max(0, Math.min(effectiveBulkCap, validConnections.length));
            const limitedConnections = validConnections.slice(0, allowedCount);

            if (allowedCount === 0) {
                showInfoToast({ message: 'Daily limit reached. Try again tomorrow.' });
                return;
            }

            if (validConnections.length > allowedCount) {
                showInfoToast({ message: `Bulk limit applied. Processing ${allowedCount} of ${validConnections.length} selected contacts.` });
            }

            // Optimistic: show "Analyzing..." in table for all we will process — race-condition-safe.
            // Also reset AI count so UI doesn't treat old results as ready.
            limitedConnections.forEach((c) => {
                if (c?._id) {
                    patchConnection(c._id, {
                        aiRecommendationsStatus: 'generating' as any,
                        linkedinAiMutualProfilesCount: 0
                    } as any);
                }
            });

            // Bulk should not auto-open the sidebar (confusing while processing).
            const toastsPollPromises: Array<Promise<{ status: string; matches: number }>> = [];
            const processedCount = limitedConnections.length;

            let succeeded = 0;
            let failed = 0;

            for (const connection of limitedConnections) {
                try {
                    // 1) Scan mutual profiles (fills linkedinMutualProfiles)
                    if (extensionInstalled && isLoggedToLinkedin) {
                        try {
                            await searchPeopleSduiHandler(connection.profileId ?? '', connection.publicIdentifier ?? '', connection._id);
                        } catch (scanError) {
                            console.warn('Mutual scan failed; generating AI recommendations anyway.', scanError);
                            showInfoToast({ message: 'Mutual scan failed. Generating AI recommendations anyway.' });
                        }
                    }

                    // 2) Kick off AI generation — backend returns immediately with status 'generating'
                    await generateAiRecommendationsHandler(connection._id);

                    succeeded += 1;

                    // 3) Poll in background — each connection gets its own poll task
                    toastsPollPromises.push(pollUntilDone(connection._id));
                } catch (error) {
                    failed += 1;
                    patchConnection(connection._id, { aiRecommendationsStatus: 'error' as any });
                    console.error('Bulk scan/generate failed for connection:', connection?._id, error);
                }
            }

            // After all background polls finish, show a single aggregated toast:
            // "X intro paths found for Y contacts"
            void Promise.allSettled(toastsPollPromises).then((results) => {
                const totals = results.reduce(
                    (acc, r) => {
                        if (r.status !== 'fulfilled') return acc;
                        acc.matches += Number(r.value?.matches ?? 0) || 0;
                        return acc;
                    },
                    { matches: 0 }
                );
                showSuccessToast({ message: `${totals.matches} intro path${totals.matches === 1 ? '' : 's'} found for ${processedCount} contact${processedCount === 1 ? '' : 's'}` });
                if (processedCount > 0) {
                    posthog.capture('find mutuals completed', {
                        source: 'bulk_actions_dropdown',
                        bulk: true,
                        selectedCount: processedCount,
                        matchesFound: totals.matches,
                    });
                }
            });

            if (succeeded > 0 && failed === 0) {
                showSuccessToast({ message: `Finding intro paths started for ${succeeded} contact${succeeded > 1 ? 's' : ''}. Results will appear shortly.` });
                return;
            }

            if (succeeded > 0 && failed > 0) {
                showInfoToast({ message: `Finding intro paths started. Success: ${succeeded}, Failed: ${failed}.` });
                return;
            }

            showErrorToast({ message: 'Failed to find intro paths for selected contacts.' });
        } catch (error) {
            console.error('Error while scanning mutual connections in bulk:', error);
            showErrorToast({ message: 'Error while finding intro paths.' });
        } finally {
            setScanMutualsLoading(false);
        }
    }, [
        scanMutualsLoading,
        selectedConnections,
        selectedAllConnections,
        getSelectedConnections,
        patchConnection,
        pollUntilDone,
        effectiveBulkCap,
        total,
        showInfoToast,
        showErrorToast,
        searchPeopleSduiHandler,
        generateAiRecommendationsHandler,
        extensionInstalled,
        isLoggedToLinkedin,
        showSuccessToast
    ])

    const clickOnRemoveHandler = async () => {
        if (showCampaignsAnnouncementIfNeeded('bulkDisconnect', bulkRemoveEnabled ? () => { void proceedWithRemove() } : undefined)) return
        if (!bulkRemoveEnabled) return
        await proceedWithRemove()
    }

    const proceedWithRemove = async () => {
        try {
            let totalConnections = user1stDegreeSelectedConnections?.length;
            let selectedForRemoval: IConnection[] = user1stDegreeSelectedConnections;

            if (selectedAllConnections) {
                // get selected connections IDs
                const result = await getSelectedConnections(total, false, true);
                selectedForRemoval = result;
                totalConnections = result?.length;
            }

            if(trial && (disconnectedConnectionsCount + user1stDegreeSelectedConnections?.length > 25)){
                const headingText = disconnectedConnectionsCount >= 25 ? 'Free Trial Limit Reached' : 'You Hit the Free Trial Limit';
                const descriptionText = disconnectedConnectionsCount >= 25 
                    ? 'You\'ve reached your free trial limit of 25 removed contacts. Upgrade to LeadDelta Pro or Starter to schedule more removals and take full control of your professional network.' 
                    : 'Free trial users can remove up to 25 contacts. Upgrade to LeadDelta Pro or Starter to schedule more removals and take full control of your professional network.';
                
                setConfirmationModal({
                    headingText,
                    descriptionText,
                    onCancelHandler: () => hideModal(),
                    onConfirmHandler: async () => {
                        hideModal();
                        window.open(AppRoutes.BILLING, '_blank');
                    },
                    confirmBtnTitle: 'Upgrade Plan'
                });

                disconnectedConnectionsCount >= 25 ? showErrorToast({message: 'Free trial limit reached.'}) : showErrorToast({message: 'You hit the free trial limit.'});
            } else {
                // Pass a stable callback that closes over the resolved list so it works even if dropdown unmounts
                const scheduleDisconnectHandler = async () => {
                    if (selectedForRemoval?.length) {
                        await scheduleConnectionsRemovalHandler(selectedForRemoval, !!selectedAllConnections);
                    }
                };

                setScheduleDisconnectModalHandler(
                    scheduleDisconnectHandler as unknown as (enteredNumOfConnections: number) => void,
                    selectedAllConnections,
                    totalConnections
                )
            }
        } catch (error) {
            console.error('Error in clickOnRemoveHandler:', error);
        }
    }

    return <DropdownMenu tooltipMessage={'More actions'} className={className} title={<SvgFragmentWrapper disabled={false}><span className="more-text">More</span><SvgFragment type='arrowDown' /></SvgFragmentWrapper>} $hideToggle>
        {[...bulkConnectionActions]
            .sort((a, b) => {
                const aIndex = CONNECTION_ACTION_MENU_ORDER.indexOf(a.slug);
                const bIndex = CONNECTION_ACTION_MENU_ORDER.indexOf(b.slug);
                return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
            })
            .map((connectionAction, index) => {
            if (!connectionAction.visible) return null;

            const dropdownItem = (
                <DropdownItem
                    $disabled={!connectionAction.enabled}
                    key={connectionAction.slug}
                    $danger={connectionAction.destructive}
                    onClickHandler={(e: any) => connectionAction.onClickHandler && connectionAction.onClickHandler()}
                >
                    {!connectionAction.showInstallExt ? (
                        <Row alignItems gap="5px">
                            <IconSlot><SvgFragment type={(!connectionAction.enabled && connectionAction.iconDisabled) ? connectionAction.iconDisabled : connectionAction.icon} /></IconSlot>
                            {connectionAction.title}
                        </Row>
                    ) : (
                        <Row alignItems spaceBetween gap="12px">
                            <Column>
                                <Row alignItems gap="5px">
                                    <IconSlot><SvgFragment type={connectionAction.icon} /></IconSlot>
                                    {connectionAction.title}
                                </Row>
                            </Column>
                            <Column>
                                <Row alignItems gap="4px">
                                    <StyledActionText icon='lock2'
                                        onClickHandler={(e: any) => {
                                            e.stopPropagation()
                                            if (workspace.isStarter) return window.open(`${window.location.origin}${AppRoutes.BILLING}`, '_blank');;
                                            if (!extensionInstalled) installExtensionHandler()
                                            if (!isExtensionVersionCorrect) updateLeadDeltaExtension()
                                            if (!isLoggedToLinkedin) openLinkedinWebsite()
                                        }}
                                    >
                                        {workspace.isStarter && (connectionAction?.slug === 'import' || connectionAction?.slug === 'export' || connectionAction?.slug === 'exportHubspot' || connectionAction?.slug === 'addToKanban') ? 'Upgrade'
                                            : !extensionInstalled ? 'Install Extension'
                                            : !isExtensionVersionCorrect ? 'Update extension'
                                            : !isLoggedToLinkedin ? 'Login to Linkedin'
                                            : ''
                                        }
                                    </StyledActionText>
                                </Row>
                            </Column>
                        </Row>
                    )}
                </DropdownItem>
            );

            if (!connectionAction.tooltipMessage) return dropdownItem;

            return (
                <TooltipWrapper key={`tt-${connectionAction.slug}`} tooltipText={connectionAction.tooltipMessage}>
                    <span style={{ display: 'block' }}>
                        {dropdownItem}
                    </span>
                </TooltipWrapper>
            );
        })}
    </DropdownMenu>
}

const SvgFragmentWrapper = styled.div<{ disabled: boolean }>`
    width: 75px;
    @media (max-width: 412px) {
        width: 64px;
    }
    @media (max-width: 400px) {
        width: 60px;
    }
    @media (max-width: 390px) {
        width: 30px;
    }
    height: 34px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${({ disabled, theme: { quinaryColor }  }) => (disabled ? quinaryColor : styles.colors.primary600)};

    .more-text {
        display: inline;
        @media (max-width: 390px) {
           display: none;
        }
    }
`
/** Fixed-width slot so titles align regardless of each icon's intrinsic svg width */
const IconSlot = styled.span`
    width: 25px;
    flex-shrink: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
`
const StyledActionText = styled(ActionText)`
    span {    
        color: ${styles.colors.black300};
        font-size: 12px;
    }

    &:hover{
        path { fill: ${styles.colors.primary400} }
    }

    div {
        margin-right: 4px;
        svg {
            margin-bottom: 4px;
        }
    }
`

export default BulkConnectionActionsDropdown
